import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, QrCode, Search } from 'lucide-react';
import { assetCategoryLabelKey, assetCategorySchema, type CreateAssetBody } from '@nbh/shared';
import { adminApi, type AdminAsset } from '../../lib/adminApi';
import { cacheFirst } from '../../lib/cacheFirst';
import { cacheAssets, getCachedAssets } from '../../lib/db';
import { RetireDialog, RetiredBadge } from './RetireDialog';

/**
 * Equipment register (SPEC.md §5.2), managed create-and-retire.
 *
 * There is no edit form on purpose: tickets name the asset they were raised against,
 * so editing one would rewrite the subject of work already recorded. Replacing a unit
 * is Duplicate & Replace — retire the old record, create its successor.
 */
/**
 * The register, cache-first (Rule 4).
 *
 * Only the default view is cached. A search result is a subset by construction and
 * storing one would leave the register looking like it holds three machines; the
 * retired-included view is a different set again. Both are still fetched normally, so
 * offline they simply come up empty rather than lying.
 */
function useAssetList(search: string, includeRetired: boolean) {
  const [assets, setAssets] = useState<AdminAsset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);
  const cacheable = !search && !includeRetired;

  const load = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = cacheFirst<AdminAsset[]>({
      readCache: cacheable ? getCachedAssets : () => Promise.resolve(null),
      load: () => adminApi.assets.list(search || undefined, includeRetired),
      writeCache: cacheable ? cacheAssets : () => Promise.resolve(),
      onValue: (rows) => {
        setAssets(rows);
        // Content on screen outranks a failure from an earlier attempt.
        setError(null);
      },
      onError: (caught) => setError(caught.message),
    });
  }, [search, includeRetired, cacheable]);

  useEffect(() => {
    load();
    return () => cancelRef.current?.();
  }, [load]);

  return { assets, error, setError, load };
}

export function AssetManager() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [includeRetired, setIncludeRetired] = useState(false);
  const [retiring, setRetiring] = useState<AdminAsset | null>(null);
  const [prefill, setPrefill] = useState<AdminAsset | null>(null);
  const { assets, error, setError, load } = useAssetList(search, includeRetired);

  return (
    <div className="space-y-4">
      <CreateAssetForm
        prefill={prefill}
        onClearPrefill={() => setPrefill(null)}
        onDone={() => {
          setPrefill(null);
          load();
        }}
        onError={setError}
      />

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex flex-1 items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3">
          <Search className="size-4 shrink-0 text-slate-500" aria-hidden="true" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('adminAssets.search')}
            aria-label={t('adminAssets.search')}
            className="min-h-[44px] flex-1 border-0 bg-transparent text-sm outline-none"
          />
        </label>

        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input
            type="checkbox"
            checked={includeRetired}
            onChange={(event) => setIncludeRetired(event.target.checked)}
            className="size-4 min-h-0"
          />
          {t('adminAssets.includeRetired')}
        </label>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      {!assets ? (
        <p className="text-sm text-slate-400">{t('admin.loading')}</p>
      ) : assets.length === 0 ? (
        <p className="text-sm text-slate-500">{t('adminAssets.empty')}</p>
      ) : (
        <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
          {assets.map((asset) => (
            <li key={asset.id} className="flex flex-wrap items-center gap-2 p-3">
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span
                    className={`truncate text-sm ${asset.isActive ? 'text-slate-100' : 'text-slate-500'}`}
                  >
                    {asset.name}
                  </span>
                  {!asset.isActive && (
                    <RetiredBadge retiredAt={asset.retiredAt} retiredReason={asset.retiredReason} />
                  )}
                </span>
                <span className="block truncate text-xs text-slate-500">
                  {t(assetCategoryLabelKey[asset.category as never])}
                  {asset.serialNumber && ` · ${asset.serialNumber}`}
                  {asset.location && ` · ${asset.location.name}`}
                </span>
              </span>

              {asset.qrCode && (
                <span className="inline-flex items-center gap-1 rounded bg-slate-800 px-2 py-1 text-xs text-sky-300">
                  <QrCode className="size-3" aria-hidden="true" />
                  {asset.qrCode}
                </span>
              )}

              <button
                type="button"
                onClick={() => setPrefill(asset)}
                className="inline-flex min-h-[40px] items-center gap-1 rounded bg-slate-800 px-3 text-xs text-slate-200"
              >
                <Copy className="size-3.5" aria-hidden="true" />
                {t('adminAssets.duplicate')}
              </button>

              {asset.isActive && (
                <button
                  type="button"
                  onClick={() => setRetiring(asset)}
                  className="min-h-[40px] rounded bg-transparent px-3 text-xs text-slate-500"
                >
                  {t('adminAssets.retire')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {retiring && (
        <RetireDialog
          subject={retiring.name}
          onCancel={() => setRetiring(null)}
          onConfirm={async (reason) => {
            await adminApi.assets.retire(retiring.id, reason);
            setRetiring(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function CreateAssetForm({
  prefill,
  onClearPrefill,
  onDone,
  onError,
}: {
  prefill: AdminAsset | null;
  onClearPrefill: () => void;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [category, setCategory] = useState<string>('OTHER');

  // Pre-fills everything except the serial number: the replacement is a different
  // physical unit, so carrying its predecessor's serial over would be wrong.
  useEffect(() => {
    if (!prefill) return;
    setName(prefill.name);
    setCategory(prefill.category);
    setSerialNumber('');
  }, [prefill]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;

    const body: CreateAssetBody = {
      name: name.trim(),
      category: category as never,
      serialNumber: serialNumber.trim() || null,
      locationId: prefill?.locationId ?? null,
      manufacturer: prefill?.manufacturer ?? null,
      model: prefill?.model ?? null,
    };

    try {
      await adminApi.assets.create(body);
      setName('');
      setSerialNumber('');
      onDone();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : 'Failed');
    }
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-800 p-3">
      <p className="mb-2 flex items-center justify-between gap-2 text-xs text-slate-400">
        <span>
          {prefill
            ? t('adminAssets.replacing', { name: prefill.name })
            : t('adminAssets.addHeading')}
        </span>
        {prefill && (
          <button
            type="button"
            onClick={onClearPrefill}
            className="min-h-0 bg-transparent p-0 text-xs text-slate-500 underline"
          >
            {t('adminAssets.clearPrefill')}
          </button>
        )}
      </p>
      <div className="flex flex-wrap gap-2">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('adminAssets.name')}
          aria-label={t('adminAssets.name')}
          className="min-w-[140px] flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
        <input
          value={serialNumber}
          onChange={(event) => setSerialNumber(event.target.value)}
          placeholder={t('adminAssets.serial')}
          aria-label={t('adminAssets.serial')}
          className="min-w-[120px] flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
        <select
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          aria-label={t('adminAssets.category')}
          className="rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        >
          {assetCategorySchema.options.map((option) => (
            <option key={option} value={option}>
              {t(assetCategoryLabelKey[option])}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={!name.trim()}
          className="min-h-[44px] rounded bg-brookfields-green ring-1 ring-brookfields-gold/70 px-4 text-sm font-medium text-white disabled:bg-brookfields-green-dark disabled:text-slate-400 disabled:ring-brookfields-gold-dark"
        >
          {t('adminAssets.add')}
        </button>
      </div>
    </form>
  );
}
